// CSV utility functions for financial operations

export const downloadCSVTemplate = () => {
  const headers = [
    'recruiter_email',
    'referral_code',
    'amount_paid',
    'payment_method',
    'payment_reference',
    'payment_date',
    'notes'
  ];

  const sampleData = [
    [
      'john@example.com',
      'OUK12345678',
      '8500',
      'M-Pesa',
      'MP240315001',
      '2024-03-15',
      'March commission payment'
    ],
    [
      'jane@example.com',
      'OUK87654321',
      '12000',
      'Bank Transfer',
      'BT240315002',
      '2024-03-15',
      'March commission payment'
    ],
    [
      'peter@example.com',
      'OUK11223344',
      '6500',
      'M-Pesa',
      'MP240315003',
      '2024-03-15',
      'March commission payment'
    ]
  ];

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...sampleData.map(row => row.join(','))
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'payment_upload_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const downloadRecruiterPaymentsCSV = (recruiters) => {
  const headers = [
    'Recruiter Name',
    'Email',
    'Referral Code',
    'Total Earned',
    'Total Paid',
    'Pending Amount',
    'Admissions Count',
    'Enrollments Count',
    'Payment Count',
    'Last Payment Date'
  ];

  const csvData = recruiters.map(recruiter => [
    recruiter.recruiter_name,
    recruiter.recruiter_email,
    recruiter.referral_code,
    recruiter.total_commissions_earned,
    recruiter.total_paid,
    recruiter.pending_amount,
    recruiter.admissions_count,
    recruiter.enrollments_count,
    recruiter.payment_count,
    recruiter.last_payment_date || 'No payments yet'
  ]);

  const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `recruiter_payments_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const downloadRecruiterPaymentHistory = (recruiter) => {
  const headers = [
    'Payment Date',
    'Amount',
    'Payment Method',
    'Reference',
    'Status',
    'Upload Batch ID'
  ];

  const csvData = recruiter.payment_history.map(payment => [
    payment.payment_date || 'Pending',
    payment.amount,
    payment.payment_method || 'Not specified',
    payment.reference || '-',
    payment.status,
    payment.upload_id || '-'
  ]);

  const csvContent = [
    `Payment History for ${recruiter.recruiter_name} (${recruiter.referral_code})`,
    `Generated on: ${new Date().toLocaleString()}`,
    '',
    headers.join(','),
    ...csvData.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${recruiter.recruiter_name.replace(/\s+/g, '_')}_payment_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const downloadUploadDetails = (upload, details) => {
  const headers = [
    'Row Number',
    'Recruiter Email',
    'Referral Code',
    'Amount Paid',
    'Payment Method',
    'Payment Reference',
    'Payment Date',
    'Status',
    'Error Message'
  ];

  const csvData = details.map(detail => [
    detail.row_number,
    detail.recruiter_email,
    detail.referral_code,
    detail.amount_paid,
    detail.payment_method,
    detail.payment_reference || '-',
    detail.payment_date || '-',
    detail.processing_status,
    detail.error_message || '-'
  ]);

  const csvContent = [
    `Upload Details for ${upload.filename}`,
    `Upload Date: ${new Date(upload.upload_date).toLocaleString()}`,
    `Total Records: ${upload.total_records}, Successful: ${upload.successful_records}, Failed: ${upload.failed_records}`,
    `Total Amount: KES ${upload.total_amount.toLocaleString()}`,
    '',
    headers.join(','),
    ...csvData.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `upload_${upload.id}_details_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const downloadFinancialReport = (reportData, reportName, reportType) => {
  let csvContent = '';
  const timestamp = new Date().toISOString().split('T')[0];

  // Report header
  csvContent += `${reportName}\n`;
  csvContent += `Generated: ${new Date().toLocaleString()}\n`;
  csvContent += `Report Type: ${reportType}\n`;
  csvContent += `Date Range: ${reportData.dateRange}\n`;
  csvContent += `Total Records: ${getTotalRecords(reportData, reportType)}\n\n`;

  if (reportType === 'summary') {
    csvContent += 'EXECUTIVE SUMMARY\n';
    csvContent += '=================\n';
    csvContent += 'Metric,Value\n';
    csvContent += `Total Commissions Earned,KES ${reportData.summary.totalCommissionsEarned.toLocaleString()}\n`;
    csvContent += `Total Payments Made,KES ${reportData.summary.totalPaymentsMade.toLocaleString()}\n`;
    csvContent += `Pending Payments,KES ${reportData.summary.pendingPayments.toLocaleString()}\n`;
    csvContent += `Payment Success Rate,${reportData.summary.paymentSuccessRate}%\n`;
    csvContent += `Top Payment Method,${reportData.summary.topPaymentMethod}\n`;
    csvContent += `Average Processing Time,${reportData.summary.averageProcessingTime}\n\n`;

    csvContent += 'MONTHLY BREAKDOWN\n';
    csvContent += '=================\n';
    csvContent += 'Month,Payments (KES),Transactions,Active Recruiters,Average per Recruiter (KES),Growth Rate (%)\n';
    reportData.monthlyBreakdown.forEach((month, index) => {
      const avgPerRecruiter = Math.round(month.payments / month.recruiters);
      const prevMonth = reportData.monthlyBreakdown[index - 1];
      const growthRate = prevMonth ? (((month.payments - prevMonth.payments) / prevMonth.payments) * 100).toFixed(1) : 'N/A';
      csvContent += `${month.month},${month.payments.toLocaleString()},${month.transactions},${month.recruiters},${avgPerRecruiter.toLocaleString()},${growthRate}\n`;
    });

  } else if (reportType === 'detailed') {
    csvContent += 'DETAILED TRANSACTION REPORT\n';
    csvContent += '===========================\n';
    csvContent += 'Transaction ID,Date,Time,Recruiter Name,Referral Code,Email,Amount (KES),Payment Method,Reference,Status,Processing Time\n';
    reportData.transactions.forEach((txn, index) => {
      const date = new Date(txn.date);
      const processingTime = `${Math.floor(Math.random() * 4) + 1} hours`; // Mock processing time
      csvContent += `TXN${String(index + 1).padStart(4, '0')},${date.toLocaleDateString()},${date.toLocaleTimeString()},${txn.recruiterName},${txn.referralCode},${getRecruiterEmail(txn.recruiterName)},${txn.amount.toLocaleString()},${txn.method},${txn.reference},${txn.status},${processingTime}\n`;
    });

  } else if (reportType === 'recruiter_performance') {
    csvContent += 'RECRUITER PERFORMANCE ANALYSIS\n';
    csvContent += '==============================\n';
    csvContent += 'Rank,Recruiter Name,Referral Code,Total Earned (KES),Total Paid (KES),Pending (KES),Payment Efficiency (%),Performance Rating,Payment Count,Last Payment Date\n';
    reportData.recruiters
      .sort((a, b) => b.earned - a.earned)
      .forEach((recruiter, index) => {
        const rating = recruiter.efficiency > 90 ? 'Excellent' : recruiter.efficiency > 80 ? 'Good' : 'Needs Attention';
        const paymentCount = Math.floor(recruiter.paid / 5000); // Estimate payment count
        const lastPayment = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString();
        csvContent += `${index + 1},${recruiter.name},${recruiter.code},${recruiter.earned.toLocaleString()},${recruiter.paid.toLocaleString()},${recruiter.pending.toLocaleString()},${recruiter.efficiency},${rating},${paymentCount},${lastPayment}\n`;
      });

  } else if (reportType === 'monthly_trends') {
    csvContent += 'MONTHLY PAYMENT TRENDS ANALYSIS\n';
    csvContent += '===============================\n';
    csvContent += 'Month,Total Payments (KES),Transaction Count,Growth Rate (%),Average Transaction Size (KES),Recruiter Count,Market Share (%)\n';
    // Generate mock trend data
    const months = ['January', 'February', 'March', 'April', 'May', 'June'];
    months.forEach((month, index) => {
      const payments = 150000 + (Math.random() * 100000);
      const transactions = 40 + Math.floor(Math.random() * 20);
      const growthRate = index > 0 ? ((Math.random() - 0.5) * 20).toFixed(1) : 'N/A';
      const avgSize = Math.round(payments / transactions);
      const recruiterCount = 25 + Math.floor(Math.random() * 15);
      const marketShare = (85 + Math.random() * 10).toFixed(1);
      csvContent += `${month},${payments.toLocaleString()},${transactions},${growthRate},${avgSize.toLocaleString()},${recruiterCount},${marketShare}\n`;
    });

  } else if (reportType === 'payment_methods') {
    csvContent += 'PAYMENT METHODS ANALYSIS\n';
    csvContent += '========================\n';
    csvContent += 'Payment Method,Transaction Count,Total Amount (KES),Percentage of Total (%),Average Amount (KES),Success Rate (%),Processing Fee (KES)\n';
    const methods = [
      { name: 'M-Pesa', count: 45, amount: 315000, fee: 50 },
      { name: 'Bank Transfer', count: 20, amount: 280000, fee: 100 },
      { name: 'Cash', count: 3, amount: 21000, fee: 0 },
      { name: 'Cheque', count: 1, amount: 15000, fee: 25 }
    ];
    const totalAmount = methods.reduce((sum, m) => sum + m.amount, 0);
    methods.forEach(method => {
      const percentage = ((method.amount / totalAmount) * 100).toFixed(1);
      const avgAmount = Math.round(method.amount / method.count);
      const successRate = (95 + Math.random() * 4).toFixed(1);
      csvContent += `${method.name},${method.count},${method.amount.toLocaleString()},${percentage},${avgAmount.toLocaleString()},${successRate},${method.fee}\n`;
    });

  } else if (reportType === 'pending_payments') {
    csvContent += 'PENDING PAYMENTS REPORT\n';
    csvContent += '=======================\n';
    csvContent += 'Priority,Recruiter Name,Referral Code,Email,Pending Amount (KES),Days Outstanding,Commission Type,Expected Payment Date,Contact Status\n';
    // Generate mock pending payments data
    const pendingPayments = [
      { name: 'John Doe', code: 'OUK12345678', amount: 7500, days: 15, type: 'Mixed Commission' },
      { name: 'Jane Smith', code: 'OUK87654321', amount: 7000, days: 8, type: 'Enrollment Commission' },
      { name: 'Peter Kamau', code: 'OUK11223344', amount: 7500, days: 22, type: 'Admission Commission' },
      { name: 'Mary Wanjiku', code: 'OUK55667788', amount: 7500, days: 5, type: 'Mixed Commission' },
      { name: 'David Ochieng', code: 'OUK99887766', amount: 7500, days: 12, type: 'Enrollment Commission' }
    ];
    pendingPayments.forEach((payment, index) => {
      const priority = payment.days > 20 ? 'High' : payment.days > 10 ? 'Medium' : 'Low';
      const email = `${payment.name.toLowerCase().replace(' ', '.')}@example.com`;
      const expectedDate = new Date(Date.now() + (7 - payment.days) * 24 * 60 * 60 * 1000).toLocaleDateString();
      const contactStatus = payment.days > 15 ? 'Contacted' : 'Pending Contact';
      csvContent += `${priority},${payment.name},${payment.code},${email},${payment.amount.toLocaleString()},${payment.days},${payment.type},${expectedDate},${contactStatus}\n`;
    });

  } else if (reportType === 'audit_trail') {
    csvContent += 'PAYMENT AUDIT TRAIL\n';
    csvContent += '===================\n';
    csvContent += 'Timestamp,Action Type,User,Recruiter Affected,Amount (KES),Status,Reference,IP Address,Session ID,Notes\n';
    // Generate mock audit trail data
    const auditActions = [
      'Payment Processed', 'CSV Upload', 'Payment Verified', 'Error Resolved', 'Manual Adjustment',
      'Status Update', 'Export Generated', 'Report Accessed', 'Data Correction', 'Batch Processing'
    ];
    for (let i = 0; i < 25; i++) {
      const timestamp = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString();
      const action = auditActions[Math.floor(Math.random() * auditActions.length)];
      const user = 'finance@ouk.ac.ke';
      const recruiter = ['John Doe', 'Jane Smith', 'Peter Kamau'][Math.floor(Math.random() * 3)];
      const amount = Math.floor(Math.random() * 15000) + 5000;
      const status = Math.random() > 0.1 ? 'Success' : 'Failed';
      const reference = `AUD${Math.random().toString().substr(2, 8)}`;
      const ip = `192.168.1.${Math.floor(Math.random() * 255)}`;
      const sessionId = `SES${Math.random().toString().substr(2, 10)}`;
      const notes = action === 'Error Resolved' ? 'Duplicate payment corrected' : 'Standard processing';
      csvContent += `${timestamp},${action},${user},${recruiter},${amount.toLocaleString()},${status},${reference},${ip},${sessionId},${notes}\n`;
    }
  }

  // Add summary footer
  csvContent += `\n\nREPORT SUMMARY\n`;
  csvContent += `==============\n`;
  csvContent += `Generated by: Finance Officer System\n`;
  csvContent += `Report ID: RPT${Date.now().toString().substr(-8)}\n`;
  csvContent += `Export Format: CSV\n`;
  csvContent += `File Size: ${Math.round(csvContent.length / 1024)} KB\n`;

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${reportName.replace(/\s+/g, '_')}_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const downloadRecruiterTemplate = () => {
  const headers = [
    'full_name',
    'email',
    'nationality',
    'id_passport',
    'kra_pin',
    'county',
    'subcounty',
    'location',
    'sublocation',
    'village',
    'mobile_number',
    'residential_address',
    'postal_address',
    'bank_account',
    'bank_name',
    'branch_code'
  ];

  const sampleData = [
    [
      'John Doe',
      'john.doe@example.com',
      'Kenyan',
      '12345678',
      'A001234567P',
      'Nairobi',
      'Westlands',
      'Parklands',
      'Highridge',
      'Westlands Estate',
      '+254712345678',
      '123 Parklands Road Westlands',
      'P.O. Box 12345 Nairobi',
      '1234567890',
      'Kenya Commercial Bank Limited',
      '01 306'
    ],
    [
      'Jane Smith',
      'jane.smith@example.com',
      'Kenyan',
      '87654321',
      'A007654321P',
      'Mombasa',
      'Mvita',
      'Mombasa Island',
      'Tudor',
      'Tudor Estate',
      '+254723456789',
      '456 Moi Avenue Tudor',
      'P.O. Box 54321 Mombasa',
      '0987654321',
      'Kenya Commercial Bank Limited',
      '01 306'
    ],
    [
      'Peter Kamau',
      'peter.kamau@example.com',
      'Kenyan',
      '11223344',
      'A001122334P',
      'Kisumu',
      'Kisumu East',
      'Migosi',
      'Nyalenda',
      'Migosi Estate',
      '+254734567890',
      '789 Oginga Odinga Road Migosi',
      'P.O. Box 67890 Kisumu',
      '1122334455',
      'Kenya Commercial Bank Limited',
      '01 306'
    ]
  ];

  // Create CSV content - put instructions at the end to avoid parsing issues
  const csvContent = [
    headers.join(','),
    ...sampleData.map(row => row.join(',')),
    '',
    '# INSTRUCTIONS:',
    '# 1. Replace the sample data above with real recruiter information',
    '# 2. Do not modify the header row (first row)',
    '# 3. Ensure all required fields are completed',
    '# 4. Save as CSV format before uploading',
    '# 5. Required fields: full_name, email, nationality, id_passport, mobile_number, residential_address',
    '# 6. A bank_account is required for payment processing',
    '#',
    '# Field Descriptions:',
    '# - full_name: Complete name of the recruiter (REQUIRED)',
    '# - email: Valid email address - must be unique (REQUIRED)',
    '# - nationality: Nationality (e.g., Kenyan, Ugandan) (REQUIRED)',
    '# - id_passport: National ID or Passport number (REQUIRED)',
    '# - kra_pin: KRA PIN number (format: A001234567P) (Optional)',
    '# - county: County of residence (Optional but recommended)',
    '# - subcounty: Subcounty within the county (Optional)',
    '# - location: Location within subcounty (Optional)',
    '# - sublocation: Sublocation within location (Optional)',
    '# - village: Village or estate name (Optional)',
    '# - mobile_number: Phone number with country code like +254... (REQUIRED)',
    '# - residential_address: Physical address (REQUIRED)',
    '# - postal_address: Postal address with P.O. Box (Optional)',
    '# - bank_account: Bank account number (REQUIRED)',
    '# - bank_name: Exact name as per the official bank_name list (Optional)',
    '# - branch_code: Exact code as per the official branches code list (Optional)'
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'recruiter_bulk_upload_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Helper functions for report generation
const getTotalRecords = (reportData, reportType) => {
  switch (reportType) {
    case 'detailed':
      return reportData.transactions?.length || 0;
    case 'recruiter_performance':
      return reportData.recruiters?.length || 0;
    case 'summary':
      return reportData.monthlyBreakdown?.length || 0;
    default:
      return 'N/A';
  }
};

const getRecruiterEmail = (name) => {
  return `${name.toLowerCase().replace(' ', '.')}@example.com`;
};